import CryptoJS from 'crypto-js';
import { createDirectUpload } from '../containers/CircleCommunityPage/CircleCommunityPage.duck';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { UploadImage } from './cloudinary';

export const recordLimit = 200;

export const excludedKeysOfCreatePost = [
  'coverImage',
  'coverImagefiles',
  'topics',
  'spaceId',
  '__pollPayload',
  '__pollClientRequestId',
];

const compareGroupNamesAlphanumerically = (nameA, nameB) =>
  (nameA || '').localeCompare(nameB || '', undefined, {
    numeric: true,
    sensitivity: 'base',
  });

export const getPostableGroupsForCreatePost = spaceListData => {
  const groups = Array.isArray(spaceListData) ? spaceListData : Object.values(spaceListData || {});

  return groups
    .filter(item => item?.is_member && item?.policies?.can_create_post)
    .sort((a, b) => compareGroupNamesAlphanumerically(a.name, b.name));
};

export const accessTypeData = [
  { label: 'Open To Everyone', value: 'open' },
  { label: 'Approve Members', value: 'private' },
  // { label: 'Secret', value: 'secret' },
];

export const renderNotificationTabs = [
  {
    id: 'notifications',
    name: 'Notifications',
  },
  {
    id: 'settings',
    name: 'Settings',
  },
];

export const renderSearchTabs = [
  {
    id: 'posts',
    name: 'Posts',
  },
  {
    id: 'comments',
    name: 'Comments',
  },
];

export const filterNotificationTypes = ['live', 'poll', 'gamification', 'new_course_content'];

//sample document test data
export const testDocumentData = [
  {
    id: 1,
    name: 'documnet 1',
    folderId: 'df28147a-fcc1-47ef-a5f9-82d8c3a4ec6d',
    groupId: '1721751',
  },
  {
    id: 2,
    name: 'documnet 2',
    folderId: 'df28147a-fcc1-47ef-a5f9-82d8c3a4ec6d',
    groupId: '1721751',
  },
  {
    id: 3,
    name: 'documnet 3',
    folderId: 'df28147a-fcc1-47ef-a5f9-82d8c3a4ec6d',
    groupId: '1721751',
  },
  {
    id: 4,
    name: 'documnet 4',
    folderId: 'df28147a-fcc1-47ef-a5f9-82d8c3a4ec6d',
    groupId: '1721751',
  },
  {
    id: 5,
    name: 'documnet 5',
    folderId: 'df28147a-fcc1-47ef-a5f9-82d8c3a4ec6d',
    groupId: '1721751',
  },
];
//test data
export const testFolderData = [
  {
    id: '67c1bf0032cefee8c8252620',
    name: 'Test - 1721751',
    folderId: 'df28147a-fcc1-47ef-a5f9-82d8c3a4ec6d',
    groupId: '1721751',
  },
  {
    id: '2',
    name: 'Test - 1721751',
    folderId: 'df28147a-fcc1-47ef-a5f9-82d8c3a4ec6d',
    groupId: '1721751',
  },
  {
    id: '3',
    name: 'Test - 1721751',
    folderId: 'df28147a-fcc1-47ef-a5f9-82d8c3a4ec6d',
    groupId: '1721751',
  },
  {
    id: '4',
    name: 'Test - 1721751',
    folderId: 'df28147a-fcc1-47ef-a5f9-82d8c3a4ec6d',
    groupId: '1721751',
  },
];

// Convert file to MD5 hash using ArrayBuffer for stability
export const convertFileToMd5 = file => {
  console.log('[MD5] Starting MD5 calculation for file:', {
    name: file.name,
    size: file.size,
    type: file.type,
  });

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = function(event) {
      const arrayBuffer = event.target?.result;
      if (!arrayBuffer) {
        console.error('[MD5] Failed to read file - arrayBuffer is null');
        reject(new Error('Failed to read file'));
        return;
      }

      console.log('[MD5] File read successfully, buffer size:', arrayBuffer.byteLength);

      const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
      const base64Md5 = CryptoJS.MD5(wordArray).toString(CryptoJS.enc.Base64);

      console.log('[MD5] MD5 checksum calculated:', base64Md5);
      resolve(base64Md5);
    };

    reader.onerror = function(error) {
      console.error('[MD5] FileReader error:', error);
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};

// Mount file data to send
export const mountFileToSend = (file, checksum) => {
  const bodyParam = {
    blob: {
      filename: file.name,
      content_type: file.type,
      byte_size: file.size,
      checksum,
    },
  };

  console.log('[MOUNT] File metadata prepared:', JSON.stringify(bodyParam, null, 2));
  return bodyParam;
};

const handleDirectUpload = async fileData => {
  try {
    const response = await fetch('https://app.circle.so/api/admin/v2/direct_uploads', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer i7EtV2Q2rpBabraghfe56zb4N19N43WQ`,
      },
      body: JSON.stringify(fileData),
    });
    console.log('response', JSON.stringify(response, null, 2));
    return response;
  } catch (error) {
    console.error('Direct upload failed:', error);
    throw error;
  }
};

// Retry helper function for handling transient failures
const retry = async (fn, retries = 2, delay = 1000) => {
  const maxRetries = 2;
  const attemptNumber = maxRetries - retries + 1;

  console.log(`[RETRY] Attempt ${attemptNumber}/${maxRetries + 1}`);

  try {
    const result = await fn();
    console.log(`[RETRY] Attempt ${attemptNumber} succeeded`);
    return result;
  } catch (err) {
    console.error(`[RETRY] Attempt ${attemptNumber} failed:`, err.message || err);

    if (retries === 0) {
      console.error('[RETRY] All retry attempts exhausted. Upload failed permanently.');
      throw err;
    }

    console.warn(`[RETRY] Waiting ${delay}ms before retry... (${retries} attempts remaining)`);
    await new Promise(resolve => setTimeout(resolve, delay));
    return retry(fn, retries - 1, delay);
  }
};

export const handleGifsUpload = async (file, dispatch) => {
  console.log('=== [GIF UPLOAD START] ===');
  console.log('[GIF] File details:', {
    name: file?.name,
    size: file?.size,
    type: file?.type,
  });

  try {
    console.log('[GIF] Step 1: Calculating MD5...');
    const md5 = await convertFileToMd5(file);

    console.log('[GIF] Step 2: Mounting metadata...');
    const fileMetadata = mountFileToSend(file, md5);

    console.log('[GIF] Step 3: Starting upload with retry...');
    const gifUploadResponse = await retry(
      () => dispatch(createDirectUpload(fileMetadata, file)),
      2
    );
    const cloudinaryResponse = await UploadImage({
      id: `${file.name}_${Date.now()}`,
      file,
      folder: 'circle-post-media',
      resourceType: 'image',
    });

    console.log('[GIF] ✓ Upload successful:', JSON.stringify(gifUploadResponse, null, 2));
    console.log('=== [GIF UPLOAD END] ===\n');
    return {
      ...gifUploadResponse,
      content_type: file.type || 'image/gif',
      filename: file.name || 'giphy.gif',
      byte_size: file.size,
      thq_media_url: cloudinaryResponse?.secure_url || cloudinaryResponse?.url || null,
      thq_media_resource_type: 'image',
    };
  } catch (error) {
    console.error('=== [GIF UPLOAD FAILED] ===');
    console.error('[GIF] Error:', error);
    console.error('=== [GIF UPLOAD FAILED END] ===\n');
    throw error;
  }
};

export const handleFileUpload = async (event, dispatch) => {
  const file = event;

  console.log('=== [UPLOAD START] ===');
  console.log('[UPLOAD] File details:', {
    name: file?.name,
    size: file?.size,
    type: file?.type,
    lastModified: file?.lastModified,
  });

  if (!file) {
    console.error('[UPLOAD] No file provided');
    throw new Error('No file provided');
  }

  try {
    console.log('[UPLOAD] Step 1: Calculating MD5 checksum...');
    const md5 = await convertFileToMd5(file);

    console.log('[UPLOAD] Step 2: Mounting file metadata...');
    const fileMetadata = mountFileToSend(file, md5);

    console.log('[UPLOAD] Step 3: Starting direct upload with retry logic...');
    const response = await retry(() => dispatch(createDirectUpload(fileMetadata, file)), 2);
    const resourceType = file.type?.startsWith('video/')
      ? 'video'
      : file.type?.startsWith('image/')
      ? 'image'
      : 'raw';
    const cloudinaryResponse = await UploadImage({
      id: `${file.name}_${Date.now()}`,
      file,
      folder: 'circle-post-media',
      resourceType,
    });

    console.log('[UPLOAD] Step 4: Validating response...');
    console.log('[UPLOAD] Response received:', JSON.stringify(response, null, 2));

    // Check if response is valid
    if (!response || !response.signed_id) {
      console.error('[UPLOAD] Invalid response - missing signed_id:', response);
      throw new Error('Invalid response from upload server');
    }

    console.log('[UPLOAD] ✓ Upload successful! signed_id:', response.signed_id);
    console.log('=== [UPLOAD END] ===\n');
    return {
      ...response,
      content_type: file.type,
      filename: file.name,
      byte_size: file.size,
      thq_media_url: cloudinaryResponse?.secure_url || cloudinaryResponse?.url || null,
      thq_media_resource_type: resourceType,
    };
  } catch (error) {
    console.error('=== [UPLOAD FAILED] ===');
    console.error('[UPLOAD] Error details:', {
      message: error.message,
      stack: error.stack,
      fileName: file?.name,
    });
    console.error('=== [UPLOAD FAILED END] ===\n');
    throw new Error(error.message || 'Failed to upload file');
  }
};

const getMediaPreviewBySignedId = (items = []) =>
  (Array.isArray(items) ? items : []).reduce((acc, item) => {
    if (item?.signed_id) {
      acc[item.signed_id] = item;
    }
    return acc;
  }, {});

const getMediaAttachmentId = media => media?.signed_id || media?.id || null;

const getRenderableMediaAttachmentId = media =>
  media?.signed_id || media?.id || media?.url || media?.thq_media_url || null;

const dedupeRenderableMediaAttachments = attachments => {
  const seenAttachmentIds = new Set();

  return attachments.filter(attachment => {
    const attachmentId = getRenderableMediaAttachmentId(attachment);

    if (!attachmentId) {
      return true;
    }

    if (seenAttachmentIds.has(attachmentId)) {
      return false;
    }

    seenAttachmentIds.add(attachmentId);
    return true;
  });
};

const createRenderableMediaAttachments = values => {
  const previewBySignedId = {
    ...getMediaPreviewBySignedId(values?.imagefiles),
    ...getMediaPreviewBySignedId(values?.videofiles),
    ...getMediaPreviewBySignedId(values?.giffiles),
    ...getMediaPreviewBySignedId(values?.documentsfiles),
  };
  const toRenderableAttachment = media => {
    if (!media) return null;
    if (typeof media === 'object' && media.content_type && (media.url || media.image_variants)) {
      return media;
    }

    const signedId = getMediaAttachmentId(media);
    const preview = previewBySignedId[signedId] || {};
    const url = media?.thq_media_url || media?.url || preview?.file || null;
    const contentType =
      media?.content_type ||
      preview?.file?.type ||
      (typeof url === 'string' && /\.(mp4|mov|webm|m4v)$/i.test(url)
        ? 'video/mp4'
        : media?.filename === 'giphy.gif'
        ? 'image/gif'
        : 'image/jpeg');

    if (!signedId && !url) return null;

    const isImage = contentType?.startsWith('image/');
    const isVideo = contentType?.startsWith('video/');

    return {
      id: signedId || url,
      signed_id: signedId,
      filename:
        media?.filename ||
        preview?.file?.name ||
        (contentType === 'image/gif' ? 'giphy.gif' : 'media'),
      content_type: contentType,
      byte_size: media?.byte_size || preview?.file?.size || null,
      url,
      image_variants: isImage
        ? {
            small: url,
            large: url,
            thumbnail: url,
          }
        : preview?.poster
        ? { thumbnail: preview.poster }
        : {},
      video_variants: isVideo
        ? {
            mp4: url,
          }
        : {},
    };
  };

  return dedupeRenderableMediaAttachments(
    [
      ...(values?.image || []),
      ...(values?.video || []),
      ...(values?.document || []),
      ...(values?.gifs || []),
    ]
      .map(toRenderableAttachment)
      .filter(Boolean)
  );
};

const getUniqueMediaAttachmentIds = values =>
  [
    ...(values?.image || []),
    ...(values?.video || []),
    ...(values?.document || []),
    ...(values?.gifs || []),
  ]
    .map(getMediaAttachmentId)
    .filter(Boolean)
    .filter((id, index, list) => list.indexOf(id) === index);

export const createPostBodyHelper = (
  values,
  formateEditor,
  currentUser,
  currentCommunityMember,
  rawContent = null,
  spaceListData = []
) => {
  const spaceId = values.spaceId;
  const allmembersNotification =
    Array.isArray(values.allmembersNotification) &&
    values.allmembersNotification.includes('allmembersNotification');
  const allmembersNotificationbySms =
    Array.isArray(values.allmembersNotificationbySms) &&
    values.allmembersNotificationbySms.includes('allmembersNotification bySms');

  // Get space name from spaceListData
  const selectedSpace = spaceListData?.find(space => space.id == spaceId);
  const spaceName = selectedSpace?.name || '';

  // Extract mentioned user SGIDs from raw content if available
  let mentionedUserSgids = [];
  if (rawContent && rawContent.entityMap) {
    Object.keys(rawContent.entityMap).forEach(key => {
      const entity = rawContent.entityMap[key];
      if (
        entity.type === 'mention' &&
        entity.data &&
        entity.data.mention &&
        entity.data.mention.sgid
      ) {
        mentionedUserSgids.push(entity.data.mention.sgid);
      }
    });
    // Remove duplicates
    mentionedUserSgids = [...new Set(mentionedUserSgids)];
  }

  const thqMediaAttachments = createRenderableMediaAttachments(values);

  const bodyParam = {
    name: values.postTitle || '',
    ...(values?.coverImage?.signed_id && { cover_image: values.coverImage.signed_id }),
    // slug: values.postTitle || '',
    body: values.postDescription || '',
    is_liking_enabled: true,
    is_comments_enabled: true,
    tiptap_body: {
      // body: {
      //   type: 'doc',
      //   content: [],
      // },
      body: formateEditor,
      // inline_attachments: [],
      attachments: [],
    },
    topics: values?.topics || [],
    thq_media_attachments: thqMediaAttachments,
  };
  // if (values?.postDescription) {
  //   bodyParam?.tiptap_body?.body.content.push({
  //     type: 'paragraph',
  //     content: values?.tagedUserData ? [
  //       {
  //         type: 'text',
  //         text: values?.postDescription,
  //         circle_ios_fallback_text: values?.postDescription,
  //       },
  //       ...values?.tagedUserData
  //     ] : [
  //       {
  //         type: 'text',
  //         text: values?.postDescription,
  //         circle_ios_fallback_text: values?.postDescription,
  //       },
  //     ],
  //   });
  // }

  if (values?.image) {
    values?.image?.forEach(image => {
      // bodyParam?.tiptap_body?.body.content.push({
      //   type: 'image',
      //   attrs: {
      //     url: image?.direct_upload.url,
      //     signed_id: image.signed_id,
      //     content_type: image.content_type,
      //     width: '100%',
      //     width_ratio: 1,
      //     alignment: 'center',
      //   },
      // });

      bodyParam?.tiptap_body?.attachments.push(image.signed_id);
    });
  }
  if (values?.video) {
    values?.video?.forEach(video => {
      bodyParam?.tiptap_body?.attachments.push(video.signed_id);
    });
  }
  if (values?.document) {
    values?.document?.forEach(document => {
      bodyParam?.tiptap_body?.attachments.push(document.signed_id);
    });
  }
  if (values?.gifs) {
    values?.gifs?.forEach(gif => {
      bodyParam?.tiptap_body?.attachments.push(gif.signed_id);
    });
  }
  // if (gifData) {
  //   gifData?.forEach(gif => {
  //     bodyParam?.tiptap_body?.body.content.push({
  //       type: 'image',
  //       attrs: {
  //         url: gif?.direct_upload.url,
  //         signed_id: gif.signed_id,
  //         content_type: gif.content_type,
  //         width: '100%',
  //         width_ratio: 1,
  //         alignment: 'center',
  //       },
  //     });
  //     bodyParam?.tiptap_body?.inline_attachments.push({
  //       content_type: gif.content_type,
  //       url: gif?.url,
  //       signed_id: gif.signed_id,
  //     });
  //   });
  // }

  const notification = allmembersNotification
    ? {
        spaceId,
        params: { spaceId, slug: 'post-page' },
        search: {},
        routeName: 'PostViewPage',
        actor_id: currentUser?.id?.uuid || '',
        actor_name: currentCommunityMember?.name || '',
        actor_avatar_url: currentCommunityMember?.avatar_url || '',
      }
    : null;

  const smsNotification = allmembersNotificationbySms
    ? {
        enabled: true,
        spaceName: spaceName,
        postTitle: values.postTitle || '',
      }
    : null;

  return {
    bodyParam,
    notification,
    mentionedUserSgids,
    authorUserId: currentCommunityMember?.headline || '',
    authorName: currentCommunityMember?.name || '',
    smsNotification,
  };
};

export const getFilteredSpaceGroup = spaceData => {
  if (!Array.isArray(spaceData)) {
    console.error('spaceData is not an array:', spaceData);
    return []; // Return an empty array as fallback
  }

  const newSpaces = spaceData.filter(item => !item.is_private)
  const filteredData = newSpaces?.reduce((acc, item) => {
    // Check if the space_group_id already exists in the accumulator
    const existingItem = acc.find(el => el.space_group_id === item.space_group_id);
    // If it doesn't exist, add the current item
    if (!existingItem) {
      acc.push(item);
    }

    return acc;
  }, []);
  return filteredData.sort((a, b) =>
    (a.space_group_name || '').localeCompare(b.space_group_name || '', undefined, {
      sensitivity: 'base',
    })
  );
};

export const formatCreatedAt = createdAt => {
  const now = moment();
  const createdMoment = moment(createdAt);
  const duration = moment.duration(now.diff(createdMoment));

  if (duration.asHours() < 23) {
    // Show minutes/hours since posted if less than 23 hours
    if (duration.asMinutes() < 60) {
      return `${Math.floor(duration.asMinutes())} min ago`;
    } else {
      return `${Math.floor(duration.asHours())} hours ago`;
    }
  } else {
    // Show the date if posted more than 23 hours ago
    return createdMoment.format('MMM DD');
  }
};

export const createCommentBodyHelper = (values, formateEditor) => {
  const thqMediaAttachments = createRenderableMediaAttachments(values);
  const attachmentIds = getUniqueMediaAttachmentIds(values);
  const bodyParam = {
    comment: {
      body: values?.commentBody,
      tiptap_body: {
        // body: {
        //   type: 'doc',
        //   content: [],
        // },
        body: formateEditor,
        attachments: [],
      },
    },
    thq_media_attachments: thqMediaAttachments,
  };
  // if (values?.commentBody) {
  //   bodyParam?.comment?.tiptap_body?.body.content.push({
  //     type: 'paragraph',
  //     content: values?.tagedUserData ? [
  //       {
  //         type: 'text',
  //         text: values?.commentBody,
  //         circle_ios_fallback_text: values?.commentBody,
  //       },
  //       ...values?.tagedUserData
  //     ] : [
  //       {
  //         type: 'text',
  //         text: values?.commentBody,
  //         circle_ios_fallback_text: values?.commentBody,
  //       },
  //     ],
  //   });
  // }
  attachmentIds.forEach(attachmentId => {
    bodyParam?.comment?.tiptap_body?.attachments.push(attachmentId);
  });
  return bodyParam;
};

export const getInitialValuesForComments = commentData => {
  const { tiptap_body } = commentData || {};

  const { body, attachments, inline_attachments, sgids_to_object_map } = tiptap_body || {};
  const sgidsToObjectMap = sgids_to_object_map || {};
  const sgidValues = Object.values(sgidsToObjectMap).filter(Boolean);
  const collectBodyAttachments = nodes => {
    if (!Array.isArray(nodes)) return [];

    return nodes.reduce((acc, node) => {
      const attrs = node?.attrs || {};
      const sgidAttachment = attrs?.sgid ? sgidsToObjectMap[attrs.sgid] : null;
      const nodeAttachment =
        node?.content_type || node?.url || node?.image_variants || node?.video_variants ? node : null;
      const attrsAttachment =
        attrs?.signed_id || attrs?.url || attrs?.content_type
          ? {
              ...attrs,
              filename: attrs.filename || attrs.file_name,
            }
          : null;

      return [
        ...acc,
        ...(sgidAttachment ? [sgidAttachment] : []),
        ...(nodeAttachment ? [nodeAttachment] : []),
        ...(attrsAttachment ? [attrsAttachment] : []),
        ...collectBodyAttachments(node?.content),
      ];
    }, []);
  };
  const resolveAttachment = attachment => {
    if (!attachment) return null;
    if (typeof attachment === 'string') {
      return (
        sgidsToObjectMap[attachment] ||
        sgidValues.find(
          item => item?.signed_id === attachment || item?.id === attachment || item?.url === attachment
        ) ||
        null
      );
    }
    if (attachment?.attrs?.sgid && sgidsToObjectMap[attachment.attrs.sgid]) {
      return sgidsToObjectMap[attachment.attrs.sgid];
    }
    return attachment;
  };
  const rawAttachments = [
    ...collectBodyAttachments(body?.content),
    ...(Array.isArray(attachments) ? attachments : []),
    ...(Array.isArray(inline_attachments) ? inline_attachments : []),
    ...sgidValues,
  ];
  const renderableAttachments = rawAttachments
    .map(resolveAttachment)
    .filter(
      attachment =>
        attachment &&
        typeof attachment === 'object' &&
        (attachment.content_type ||
          attachment.url ||
          attachment.thq_media_url ||
          attachment.image_variants ||
          attachment.video_variants ||
          attachment.filename)
    )
    .filter((attachment, index, list) => {
      const identity =
        attachment.signed_id ||
        attachment.id ||
        attachment.url ||
        attachment.thq_media_url ||
        attachment.filename;
      return identity ? list.findIndex(item => {
        const itemIdentity =
          item.signed_id || item.id || item.url || item.thq_media_url || item.filename;
        return itemIdentity === identity;
      }) === index : true;
    });
  const { content } = body || {};
  const paragraphItem = content?.find(item => item.type === 'paragraph');
  const textValue = paragraphItem?.content?.[0]?.text || null;
  const getAttachmentUrl = attachment => {
    const isVideo = attachment?.content_type?.startsWith('video/');
    if (isVideo) {
      return (
        attachment?.video_variants?.mp4 ||
        attachment?.video_variants?.hls ||
        attachment?.url ||
        attachment?.thq_media_url ||
        ''
      );
    }
    return (
      attachment?.url ||
      attachment?.thq_media_url ||
      attachment?.image_variants?.large ||
      attachment?.image_variants?.small ||
      attachment?.image_variants?.thumbnail ||
      attachment?.video_variants?.mp4 ||
      attachment?.video_variants?.hls ||
      ''
    );
  };
  const getAttachmentSignedId = attachment =>
    attachment?.signed_id || attachment?.id || getAttachmentUrl(attachment);
  const toPreviewFile = attachment => ({
    file: getAttachmentUrl(attachment),
    name: attachment?.filename || 'Attachment',
    signed_id: getAttachmentSignedId(attachment),
    poster: attachment?.image_variants?.thumbnail || '',
  });
  const getAttachmentNameOrUrl = attachment =>
    `${attachment?.filename || ''} ${attachment?.url || ''} ${attachment?.thq_media_url || ''}`;
  const isGifAttachment = attachment =>
    attachment?.content_type === 'image/gif' || /\.gif($|\?)/i.test(getAttachmentNameOrUrl(attachment));
  const isImageAttachment = attachment =>
    (attachment.content_type?.startsWith('image/') ||
      /\.(png|jpe?g|webp|bmp|svg)($|\?)/i.test(getAttachmentNameOrUrl(attachment)) ||
      attachment.image_variants) &&
    !isGifAttachment(attachment);
  const isVideoAttachment = attachment =>
    (attachment.content_type?.startsWith('video/') ||
      /\.(mp4|mov|webm|m4v)($|\?)/i.test(getAttachmentNameOrUrl(attachment)) ||
      attachment.video_variants) &&
    !isGifAttachment(attachment);
  const isDocumentAttachment = attachment =>
    attachment.content_type?.startsWith('application/') ||
    attachment.content_type?.startsWith('text/') ||
    /\.(pdf|docx?|xlsx?|pptx?|txt|csv)($|\?)/i.test(getAttachmentNameOrUrl(attachment));

  const imageAttachments = renderableAttachments?.filter(isImageAttachment);

  const videoAttachments = renderableAttachments?.filter(isVideoAttachment);

  const documentAttachments = renderableAttachments?.filter(isDocumentAttachment);

  const gifAttachments = renderableAttachments?.filter(isGifAttachment);

  return {
    // commentBody: textValue,
    image: imageAttachments || [],
    video: videoAttachments || [],
    document: documentAttachments || [],
    gifs: gifAttachments || [],
    documentsfiles: (documentAttachments || []).map(toPreviewFile),
    giffiles: (gifAttachments || []).map(toPreviewFile),
    imagefiles: (imageAttachments || []).map(toPreviewFile),
    videofiles: (videoAttachments || []).map(toPreviewFile),
  };
};

export const getInitialValuesForEditPost = postData => {
  const { body, attachments = [] } = postData?.tiptap_body || {};
  const renderableAttachments = Array.isArray(attachments)
    ? attachments.filter(attachment => attachment && typeof attachment === 'object')
    : [];
  const { content } = body || {};
  const paragraphItem = content?.find(item => item.type === 'paragraph');
  const textValue = paragraphItem?.content?.[0]?.text || null;
  const selectedTopics = Array.isArray(postData?.topics)
    ? postData.topics.map(topic => (typeof topic === 'object' ? topic.id : topic)).filter(Boolean)
    : [];

  const getAttachmentUrl = attachment =>
    attachment?.url ||
    attachment?.thq_media_url ||
    attachment?.image_variants?.large ||
    attachment?.image_variants?.small ||
    attachment?.image_variants?.thumbnail ||
    attachment?.video_variants?.mp4 ||
    attachment?.video_variants?.hls ||
    '';
  const getAttachmentSignedId = attachment =>
    attachment?.signed_id || attachment?.id || getAttachmentUrl(attachment);
  const toPreviewFile = attachment => ({
    file: getAttachmentUrl(attachment),
    signed_id: getAttachmentSignedId(attachment),
    poster: attachment?.image_variants?.thumbnail || '',
  });
  const isGifAttachment = attachment =>
    attachment?.content_type === 'image/gif' || attachment?.filename?.endsWith('.gif');

  const imageAttachments = renderableAttachments?.filter(
    attachment => attachment.content_type?.startsWith('image/') && !isGifAttachment(attachment)
  );

  const videoAttachments = renderableAttachments?.filter(
    attachment =>
      attachment.content_type?.startsWith('video/') && !attachment.filename?.endsWith('.gif')
  );

  const documentAttachments = renderableAttachments?.filter(attachment =>
    attachment.content_type?.startsWith('application/')
  );

  const gifAttachments = renderableAttachments?.filter(isGifAttachment);
  return {
    spaceId: postData?.space?.id || '',
    postTitle: postData?.name || '',
    postDescription: textValue,
    image: imageAttachments || [],
    video: videoAttachments || [],
    document: documentAttachments || [],
    gifs: gifAttachments || [],
    documentsfiles: documentAttachments || [],
    giffiles: (gifAttachments || []).map(toPreviewFile),
    imagefiles: (imageAttachments || []).map(toPreviewFile),
    videofiles: (videoAttachments || []).map(toPreviewFile),
    topics: selectedTopics,
    coverImage: {},
    coverImagefiles: {},
    allmembersNotification: [],
    allmembersNotificationbySms: [],
    notificationOption: 'none',
  };
};

export const reportReasons = [
  {
    id: 'harassment',
    name: 'Harassment',
  },
  {
    id: 'spam',
    name: 'Spam',
  },
  {
    id: 'incorrect_location',
    name: 'Incorrect space/post',
  },
  {
    id: 'against_guidelines',
    name: 'Against community guidelines',
  },
  {
    id: 'other',
    name: 'Other',
  },
];

export function abbreviateName(fullName) {
  const nameParts = fullName?.split(' ').filter(Boolean); // Split and remove empty parts
  if (nameParts?.length === 0) return ''; // Return empty string if no valid name parts
  if (nameParts?.length === 1) return nameParts?.[0][0].toUpperCase(); // Single name case

  return nameParts?.[0][0].toUpperCase() + nameParts?.[nameParts.length - 1][0].toUpperCase(); // First and last initials
}

export const mergeSpaceArraysById = (array1 = [], array2 = []) =>
  Array.from(
    [...(Array.isArray(array1) ? array1 : []), ...(Array.isArray(array2) ? array2 : [])]
      .reduce((map, item) => map.set(item.id, { ...map.get(item.id), ...item }), new Map())
      .values()
  );

export const updatePostFormValues = (event, setFormOldValues, type) => {
  return setFormOldValues(prevState => {
    const updatedValues = {
      ...prevState,
      [type]: event,
    };

    return updatedValues;
  });
};

export const groupSortOptions = [
  { value: 'latest', label: 'Latest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'alphabetical', label: 'Alphabetical' },
  // { value: 'popular', label: 'Popular' },
  // { value: 'active', label: 'Active' },
  // { value: 'likes', label: 'Likes' },
  // { value: 'latest_updated', label: 'Latest Updated' },
  // { value: 'oldest_updated', label: 'Oldest Updated' },
  // { value: 'latest_profile_confirmed', label: 'Latest Profile Confirmed' },
];

export const calculateTotalPages = (count = 0, perPage = 1) =>
  perPage > 0 ? Math.ceil(count / perPage) : 0;

export const getSlugFromUrl = url => {
  const pathSegments = new URL(url).pathname.split('/');
  const slug = pathSegments[2];
  return slug;
};

//new transform function
export const transformRawContent = rawData => {
  const mentionRegex = /@([\w]+)/g; // Detect mentions
  const urlRegex = /(?:(?<!\S)(https?:\/\/|www\.)[^\s@]+)/g;
  // const urlRegex = /(https?:\/\/[^\s]+)/g; // Detect URLs

  const parseBlock = block => {
    const { text, type, entityRanges, inlineStyleRanges = [] } = block;
    const content = [];
    let lastIndex = 0;

    // Create a map of entityRanges for quick lookup
    const mentionEntities = {};
    entityRanges.forEach(({ key, offset, length }) => {
      mentionEntities[offset] = { key, length };
    });

    // Create a map of inline styles for quick lookup
    const styleMap = {};
    inlineStyleRanges.forEach(({ style, offset, length }) => {
      for (let i = offset; i < offset + length; i++) {
        if (!styleMap[i]) {
          styleMap[i] = [];
        }
        styleMap[i].push(style);
      }
    });

    // Helper function to get marks for a character position
    const getMarksForPosition = position => {
      const styles = styleMap[position] || [];
      const marks = [];

      if (styles.includes('BOLD')) {
        marks.push({ type: 'bold' });
      }
      if (styles.includes('ITALIC')) {
        marks.push({ type: 'italic' });
      }
      if (styles.includes('UNDERLINE')) {
        marks.push({ type: 'underline' });
      }
      if (styles.includes('CODE')) {
        marks.push({ type: 'code' });
      }

      return marks;
    };

    // Helper function to process text with styles
    const processTextWithStyles = (textSegment, startPos) => {
      const segments = [];
      let currentText = '';
      let currentMarks = null;

      for (let i = 0; i < textSegment.length; i++) {
        const charPos = startPos + i;
        const marks = getMarksForPosition(charPos);
        const marksKey = JSON.stringify(marks);

        if (currentMarks === null) {
          currentMarks = marksKey;
          currentText = textSegment[i];
        } else if (currentMarks === marksKey) {
          currentText += textSegment[i];
        } else {
          // Style changed, push current segment
          const textNode = {
            type: 'text',
            text: currentText,
            circle_ios_fallback_text: currentText,
          };
          if (currentMarks !== '[]') {
            textNode.marks = JSON.parse(currentMarks);
          }
          segments.push(textNode);

          currentText = textSegment[i];
          currentMarks = marksKey;
        }
      }

      // Push remaining text
      if (currentText) {
        const textNode = {
          type: 'text',
          text: currentText,
          circle_ios_fallback_text: currentText,
        };
        if (currentMarks !== '[]') {
          textNode.marks = JSON.parse(currentMarks);
        }
        segments.push(textNode);
      }

      return segments;
    };

    // Iterate through text to identify mentions and links
    for (let i = 0; i < text.length; i++) {
      const entity = mentionEntities[i];

      if (entity) {
        // Add preceding text before the mention
        if (i > lastIndex) {
          const precedingText = text.slice(lastIndex, i);
          content.push(...processTextWithStyles(precedingText, lastIndex));
        }

        // Add mention
        const mentionText = text.slice(i, i + entity.length);
        const entityData = rawData.entityMap[entity.key];

        if (entityData && entityData.type === 'mention') {
          content.push({
            type: 'mention',
            attrs: {
              sgid: entityData?.data?.mention?.sgid,
            },
            circle_ios_fallback_text: mentionText,
          });
        } else {
          content.push({
            type: 'text',
            text: mentionText,
            circle_ios_fallback_text: mentionText,
          });
        }

        lastIndex = i + entity.length;
        i += entity.length - 1; // Skip processed characters
      }
    }

    // Handle remaining text and extract links
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      let match;
      let currentIndex = 0;
      const urlMatches = [];

      // Collect all URL matches first
      while ((match = urlRegex.exec(remainingText)) !== null) {
        urlMatches.push({
          start: match.index,
          end: match.index + match[0].length,
          url: match[0],
        });
      }

      if (urlMatches.length > 0) {
        // Process text with URLs
        urlMatches.forEach((urlMatch, idx) => {
          const matchStart = urlMatch.start;
          const matchEnd = urlMatch.end;

          // Add text before the URL with styles
          if (currentIndex < matchStart) {
            const textBeforeUrl = remainingText.slice(currentIndex, matchStart);
            content.push(...processTextWithStyles(textBeforeUrl, lastIndex + currentIndex));
          }

          // Add URL as a link with styles
          const urlMarks = getMarksForPosition(lastIndex + matchStart);
          const linkMark = {
            type: 'link',
            attrs: {
              href: urlMatch.url,
              target: '_blank',
            },
          };

          content.push({
            type: 'text',
            marks: [...urlMarks, linkMark],
            text: urlMatch.url,
          });

          currentIndex = matchEnd;
        });

        // Add remaining text after the last URL with styles
        if (currentIndex < remainingText.length) {
          const finalText = remainingText.slice(currentIndex);
          content.push(...processTextWithStyles(finalText, lastIndex + currentIndex));
        }
      } else {
        // No URLs found, process all remaining text with styles
        content.push(...processTextWithStyles(remainingText, lastIndex));
      }
    }

    // Handle block types
    switch (type) {
      case 'unstyled':
        return { type: 'paragraph', content };
      case 'unordered-list-item':
        return {
          type: 'bulletList',
          content: [
            {
              type: 'listItem',
              content: [{ type: 'paragraph', content }],
            },
          ],
        };
      case 'ordered-list-item':
        return {
          type: 'orderedList',
          attrs: { start: 1 },
          content: [
            {
              type: 'listItem',
              content: [{ type: 'paragraph', content }],
            },
          ],
        };
      case 'header-two':
        return { type: 'heading', attrs: { level: 2 }, content };
      case 'header-three':
        return { type: 'heading', attrs: { level: 3 }, content };
      case 'blockquote':
        return { type: 'blockquote', content };
      default:
        return { type: 'paragraph', content };
    }
  };

  // Transform each block
  const content = rawData.blocks.map(parseBlock);

  return {
    type: 'doc',
    content,
  };
};

// export const transformRawContent = (rawData) => {
//   const mentionRegex = /@([\w]+)/g; // Capture only alphanumeric characters after '@'

//   const parseBlock = (block) => {
//     const { text, type, entityRanges } = block;
//     const content = [];
//     let lastIndex = 0;
//     let match;

//     // Create a map of entityRanges for quick lookup
//     const mentionEntities = {};
//     entityRanges.forEach(({ key, offset, length }) => {
//       mentionEntities[offset] = { key, length };
//     });

//     while ((match = mentionRegex.exec(text)) !== null) {
//       const mentionIndex = match.index;
//       const mentionText = match[0];

//       // Add preceding text before mention
//       if (mentionIndex > lastIndex) {
//         const precedingText = text.slice(lastIndex, mentionIndex);
//         content.push({
//           type: "text",
//           text: precedingText,
//           circle_ios_fallback_text: precedingText,
//         });
//       }

//       const entity = mentionEntities[mentionIndex];
//       if (entity) {
//         const entityData = rawData.entityMap[entity.key];
//         if (entityData && entityData.type === "MENTION") {
//           content.push({
//             type: "mention",
//             attrs: {
//               sgid: entityData.data.sgid,
//             },
//             circle_ios_fallback_text: mentionText,
//           });
//         } else {
//           // Fallback to plain text if entity data is missing or incorrect
//           content.push({
//             type: "text",
//             text: mentionText,
//             circle_ios_fallback_text: mentionText,
//           });
//         }
//       } else {
//         content.push({
//           type: "text",
//           text: mentionText,
//           circle_ios_fallback_text: mentionText,
//         });
//       }

//       lastIndex = mentionIndex + mentionText.length; // Update lastIndex to include the length of the mention
//     }

//     // Add remaining text after the mention
//     const remainingText = text.slice(lastIndex);
//     if (remainingText) {
//       content.push({
//         type: "text",
//         text: remainingText,
//         circle_ios_fallback_text: remainingText,
//       });
//     }

//     // Handle block type
//     if (type === "unstyled") {
//       return { type: "paragraph", content };
//     } else if (type === "unordered-list-item") {
//       return {
//         type: "bulletList",
//         content: [
//           {
//             type: "listItem",
//             content: [{ type: "paragraph", content }],
//           },
//         ],
//       };
//     } else if (type === "ordered-list-item") {
//       return {
//         type: "orderedList",
//         attrs: { start: 1 },
//         content: [
//           {
//             type: "listItem",
//             content: [{ type: "paragraph", content }],
//           },
//         ],
//       };
//     } else if (type === "header-two") {
//       return {
//         type: "heading",
//         attrs: { level: 2 },
//         content,
//       };
//     }
//     else if (type === "header-three") {
//       return {
//         type: "heading",
//         attrs: { level: 3 },
//         content,
//       };
//     } else if (type === "blockquote") {
//       // Handle blockquote specifically
//       return {
//         type: "blockquote",
//         content,
//       };
//     }

//     // Default to paragraph if type is not explicitly handled
//     return { type: "paragraph", content };
//   };

//   // Transform each block
//   const content = rawData.blocks.map(parseBlock);

//   return {
//     type: "doc",
//     content,
//   };
// };

export const editorTypes = [
  { label: 'Heading 2', key: 'header-two' },
  { label: 'Heading 3', key: 'header-three' },
  { label: 'Bold', key: 'BOLD' },
  { label: 'Bulleted list', key: 'unordered-list-item' },
  // { label: "Blockquote", key: "blockquote" },
  // { label: "Code Block", key: "code-block" },
];

//updated reverseTransformContent
export const reverseTransformContent = desiredJSON => {
  const parseNode = node => {
    if (!node) {
      return null;
    }

    switch (node.type) {
      case 'text':
        return { text: node.text || '', length: (node.text || '').length, entity: null };
      case 'mention': {
        const mentionText = node.circle_ios_fallback_text || '';
        return {
          text: mentionText,
          length: mentionText.length,
          entity: {
            type: 'mention',
            mutability: 'SEGMENTED',
            data: { mention: { sgid: node.attrs?.sgid } },
          },
        };
      }
      default:
        return null;
    }
  };

  const parseContent = content => {
    let text = '';
    const entityRanges = [];
    const entityMap = {};
    let entityCounter = 0;

    const nodes = Array.isArray(content) ? content : [];

    nodes.forEach(node => {
      const parsedNode = parseNode(node);
      if (parsedNode) {
        // Append text
        const startOffset = text.length;
        text += parsedNode.text;

        // Handle entities (mentions)
        if (parsedNode.entity) {
          entityMap[entityCounter] = parsedNode.entity;
          entityRanges.push({
            offset: startOffset,
            length: parsedNode.length,
            key: entityCounter,
          });
          entityCounter += 1;
        }
      } else if (Array.isArray(node?.content)) {
        const nestedContent = parseContent(node.content);
        text += nestedContent.text;
      }
    });

    return { text, entityRanges, entityMap };
  };

  const parseBlock = block => {
    const { content } = block;
    const { text, entityRanges, entityMap } = parseContent(content);

    let type = 'unstyled';
    switch (block.type) {
      case 'paragraph':
        type = 'unstyled';
        break;
      case 'bulletList':
        type = 'unordered-list-item';
        break;
      case 'orderedList':
        type = 'ordered-list-item';
        break;
      case 'heading':
        type = block.attrs.level === 2 ? 'header-two' : 'header-three';
        break;
      case 'blockquote':
        type = 'blockquote';
        break;
    }

    return {
      block: {
        key: Math.random()
          .toString(36)
          .substr(2, 5), // Generate random key
        text,
        type,
        depth: 0,
        inlineStyleRanges: [],
        entityRanges,
        data: {},
      },
      entityMap,
    };
  };

  const blocks = [];
  let finalEntityMap = {};

  const desiredContent = Array.isArray(desiredJSON?.content) ? desiredJSON.content : [];

  desiredContent.forEach(block => {
    const { block: parsedBlock, entityMap } = parseBlock(block);
    blocks.push(parsedBlock);

    // Merge entity maps
    finalEntityMap = { ...finalEntityMap, ...entityMap };
  });

  return {
    blocks: blocks.length
      ? blocks
      : [
          {
            key: Math.random()
              .toString(36)
              .substr(2, 5),
            text: '',
            type: 'unstyled',
            depth: 0,
            inlineStyleRanges: [],
            entityRanges: [],
            data: {},
          },
        ],
    entityMap: finalEntityMap,
  };
};

// export const reverseTransformContent = (structuredData) => {
//   const parseContent = (node) => {
//     let text = "";
//     const entityRanges = [];
//     const entityMap = {};

//     node.content.forEach((child, index) => {
//       if (child.type === "text") {
//         text += child.text || "";
//       } else if (child.type === "mention") {
//         const mentionText = child.circle_ios_fallback_text || "";
//         const offset = text.length;
//         const length = mentionText.length;
//         text += mentionText;

//         // Add entity for the mention
//         const entityKey = Object.keys(entityMap).length;
//         entityRanges.push({ key: entityKey, offset, length });
//         entityMap[entityKey] = {
//           type: "MENTION",
//           mutability: "IMMUTABLE",
//           data: {
//             sgid: child.attrs.sgid,
//           },
//         };
//       }
//     });

//     return { text, entityRanges, entityMap };
//   };

//   const parseBlock = (block) => {
//     if (block.type === "paragraph" || block.type === "heading") {
//       const { text, entityRanges, entityMap } = parseContent(block);
//       return {
//         block: {
//           type: block.type === "paragraph" ? "unstyled" : "header-two",
//           text,
//           entityRanges,
//         },
//         entityMap,
//       };
//     } else if (block.type === "bulletList") {
//       const listItem = block.content[0]; // Assume one listItem per bulletList
//       const { text, entityRanges, entityMap } = parseContent(listItem.content[0]);
//       return {
//         block: {
//           type: "unordered-list-item",
//           text,
//           entityRanges,
//         },
//         entityMap,
//       };
//     } else if (block.type === "orderedList") {
//       const listItem = block.content[0];
//       const { text, entityRanges, entityMap } = parseContent(listItem.content[0]);
//       return {
//         block: {
//           type: "ordered-list-item",
//           text,
//           entityRanges,
//         },
//         entityMap,
//       };
//     } else if (block.type === "blockquote") {
//       const { text, entityRanges, entityMap } = parseContent(block);
//       return {
//         block: {
//           type: "blockquote",
//           text,
//           entityRanges,
//         },
//         entityMap,
//       };
//     }

//     // Default case for unhandled types
//     const { text, entityRanges, entityMap } = parseContent(block);
//     return {
//       block: {
//         type: "unstyled",
//         text,
//         entityRanges,
//       },
//       entityMap,
//     };
//   };

//   let blocks = [];
//   let entityMap = {};

//   structuredData?.content.forEach((block) => {
//     const { block: parsedBlock, entityMap: blockEntityMap } = parseBlock(block);
//     blocks.push(parsedBlock);

//     // Merge entity maps
//     Object.keys(blockEntityMap).forEach((key) => {
//       entityMap[Object.keys(entityMap).length] = blockEntityMap[key];
//     });
//   });

//   return {
//     blocks,
//     entityMap,
//   };
// };

export const getFirstImageItem = tiptap_body => {
  // Check the attachments array first
  const firstImageFromAttachments = tiptap_body?.attachments?.find(item =>
    item?.content_type?.startsWith('image')
  );

  // If not found in attachments, check inline_attachments
  if (firstImageFromAttachments) {
    return firstImageFromAttachments;
  }

  const firstImageFromInlineAttachments = tiptap_body?.inline_attachments?.find(item =>
    item?.content_type?.startsWith('image')
  );

  // Return the result from inline_attachments or null if not found
  return firstImageFromInlineAttachments || null;
};

export const formatDataForReplyComment = (jsonData, author) => {
  const mentionData = {
    type: 'mention',
    attrs: {
      sgid: author?.rich_text_field_sgid,
    },
    circle_ios_fallback_text: `@${author?.name}`,
  };
  const contentArray = jsonData?.comment?.tiptap_body?.body?.content;

  // Check if the first item is a paragraph
  if (contentArray.length > 0 && contentArray[0].type === 'paragraph') {
    // Prepend custom data to the first item's content array
    contentArray[0].content.unshift(mentionData);
  } else {
    // Create a new paragraph object with custom data
    const newParagraph = {
      type: 'paragraph',
      content: [mentionData],
    };
    // Prepend the new paragraph to the content array
    contentArray.unshift(newParagraph);
  }
  return jsonData;
};

export const getRandomItemWithPriority = (primaryJSON, secondaryJSON, bannerJson) => {
  // Helper function to filter and get a random item
  const getRandomItemFromArray = (jsonArray, excludeJson) => {
    const filteredArray = jsonArray?.filter(
      item => JSON.stringify(item) !== JSON.stringify(excludeJson)
    );

    if (filteredArray.length === 0) {
      return null; // No suitable items found
    }

    const randomIndex = Math.floor(Math.random() * filteredArray.length);
    return filteredArray[randomIndex];
  };

  // Try to get a random item from primary JSON
  const randomItemFromPrimary = getRandomItemFromArray(primaryJSON, bannerJson);
  if (randomItemFromPrimary) {
    return randomItemFromPrimary;
  }

  // If no item found in primary JSON, try secondary JSON
  return getRandomItemFromArray(secondaryJSON, bannerJson);
};
